import { useEffect } from 'react';

export default function useReadingChapters(containerRef, content) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !content) return;
    const chapters = Array.from(container.querySelectorAll('.bible-reading-chapter'));
    if (!chapters.length) return;
    let frame;
    let disposed = false;
    const measure = () => {
      cancelAnimationFrame(frame);
      // Width/font changes invalidate remembered offscreen heights. Read all
      // heights together after removing skipping, then write them together.
      chapters.forEach((chapter) => chapter.classList.remove('bible-reading-chapter--measured'));
      frame = requestAnimationFrame(() => {
        const heights = chapters.map((chapter) => chapter.getBoundingClientRect().height);
        chapters.forEach((chapter, index) => {
          chapter.style.containIntrinsicBlockSize = `auto ${heights[index]}px`;
          chapter.classList.add('bible-reading-chapter--measured');
        });
      });
    };
    measure();
    let width = container.getBoundingClientRect().width;
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((entries) => {
      const nextWidth = entries[0].contentRect.width;
      if (nextWidth !== width) {
        width = nextWidth;
        measure();
      }
    });
    resize?.observe(container);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.classList.toggle('bible-reading-chapter--nearby', isIntersecting);
      });
    }, { rootMargin: '800px 0px' });
    chapters.forEach((chapter) => observer?.observe(chapter));
    const fontsChanged = () => { if (!disposed) measure(); };
    document.fonts?.addEventListener('loadingdone', fontsChanged);
    window.addEventListener('resize', measure);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resize?.disconnect();
      observer?.disconnect();
      document.fonts?.removeEventListener('loadingdone', fontsChanged);
      window.removeEventListener('resize', measure);
      chapters.forEach((chapter) => {
        chapter.classList.remove('bible-reading-chapter--measured', 'bible-reading-chapter--nearby');
        chapter.style.removeProperty('contain-intrinsic-block-size');
      });
    };
  }, [containerRef, content]);
}
